import * as _ from 'underscore';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Order } from '../helpers/Order';
import { ServerFilterClauses } from '../helpers/ServerFilterClauses';
import { FieldValuesQuery } from '../resources/FieldValuesQuery';
import { IListAndFieldValuesResource } from '../resources/Resource';
import { IResourceQuery, ResourceQuery } from '../resources/ResourceQuery';
import { IFieldFilterClause, ILeafFilterClause } from '../ServerApi/types/IFilterClause';
import { IGetItemsParams } from '../ServerApi/types/IGetItemsParams';
import { ContentFilterKeyName, IContentFilters } from './ContentFilters';
import { IContentType } from './ContentTypes';

// All the different place names across the app are represented by this type, to help make sure they're unique
export type PlaceName = 'project' | 'workbook' | 'datasource' | 'site' | 'sites' | 'siteUser' | 'task' | 'flow';

export interface IPlace {
  name: PlaceName;
  subplace(name: string): ISubplace<any>;
  subplaces(): ISubplace<any>[];
}

export interface ISubplace<T> {
  name: string;
  type: IContentType;
  resource: IListAndFieldValuesResource<T>;
  defaultFilters: _.Dictionary<string>;
  filterKeys(): ContentFilterKeyName[];
  query(): IResourceQuery<T>;
  filterValuesQuery(field: string): FieldValuesQuery;
}

export class Place implements IPlace {

  private subplaceMap: _.Dictionary<ISubplace<any>>;
  private subplaceList: ISubplace<any>[];

  constructor(public name: PlaceName) {
    this.subplaceMap = {};
    this.subplaceList = [];
  }

  public subplace(name: string): ISubplace<any> {
    return this.subplaceMap[name];
  }

  public subplaces(): ISubplace<any>[] {
    return this.subplaceList;
  }

  public addSubplace(subplace: ISubplace<any>) {
    this.subplaceMap[subplace.name] = subplace;
    this.subplaceList.push(subplace);
  }

}

export class Subplace<T> implements ISubplace<T> {

  constructor(
    public  name: string,
    public  type: IContentType,
    private resourceQueryFn: () => IResourceQuery<T>,
    private filterValuesQueryFn: (filter: string) => FieldValuesQuery,
    public  resource: IListAndFieldValuesResource<T>,
    public  defaultFilters: Partial<IContentFilters<string>>,
    private excludeFilters: ContentFilterKeyName[] = []
  ) { }

  public filterKeys(): ContentFilterKeyName[] {
    return _.difference(this.type.filterKeys, this.excludeFilters);
  }

  public query(): IResourceQuery<T> {
    return this.resourceQueryFn();
  }

  public filterValuesQuery(field: string): FieldValuesQuery {
    return this.filterValuesQueryFn ? this.filterValuesQueryFn(field) : undefined;
  }

  // creates an empty place that never has any content.
  // this is only used on the site users place when we a showing a place for a server admin
  // who is not a site member.
  public static empty<T>(
      name: string,
      type: IContentType,
      $q: ng.IQService) {
    const emptyResource = new ResourceQuery({
      fetchList: (params: IGetItemsParams) => {
        return $q.when({
          items: <T[]>[],
          index: 0,
          total: 0
        });
      }
    });

    return new Subplace<T>(
      name,
      type,
      () => {
       return emptyResource;
      },
      undefined,
      {
        all: () => emptyResource,
      },
      {});
  }

  // TODO: refactor this method to use QueryService
  public static create<T>(
      name: string,
      type: IContentType,
      resource: IListAndFieldValuesResource<T>,
      urlParamsFn: () => _.Dictionary<any>,
      defaultFilters: Partial<IContentFilters<string>> = {},
      excludeFilters: ContentFilterKeyName[] = []
  ) {

    function getFilterClauses(urlParams: _.Dictionary<any>) {
      const relevantFilterKeys = _.difference(type.filterKeys, excludeFilters);
      const relevantUrlFilters = _(urlParams).pick(relevantFilterKeys);
      return ServerFilterClauses.create(_.defaults(relevantUrlFilters, defaultFilters));
    }

    return new Subplace<T>(
      name,
      type,
      () => {
        const urlParams = urlParamsFn();
        const filterClauses = getFilterClauses(urlParams);
        const order = Order.fromInput(urlParams[UrlParamKeys.Order], type.orderKeys) || type.defaultOrder;
        return resource.all().withFiltersAndOrder(filterClauses, order);
      },
      (!resource.valuesForField) ? undefined : (field: string) => {
        const filterClausesFromUrl = getFilterClauses(urlParamsFn());

        // Remove the filter for the field that we're changing so that we don't filter for both values
        const filterClausesWithoutCurrentField = _.reject(filterClausesFromUrl, (filter: ILeafFilterClause) => {
          const fieldFilter = <IFieldFilterClause> filter;
          return field && fieldFilter.field === field;
        });

        return resource.valuesForField(field).withFilters(filterClausesWithoutCurrentField);
      },
      resource,
      defaultFilters,
      excludeFilters
    );
  }

}
